import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as Ccs2Icon } from '../../assets/device-hub/connecter-type/ccs2.svg';
import { ReactComponent as ChademoIcon } from '../../assets/device-hub/connecter-type/CHAdeMO.svg';
import { ReactComponent as J1772Icon } from '../../assets/device-hub/connecter-type/J1172.svg';
import { ReactComponent as NacsIcon } from '../../assets/device-hub/connecter-type/Nacs.svg';
import { ReactComponent as Type2Icon } from '../../assets/device-hub/connecter-type/type2.svg';
import { ReactComponent as FlashIcon } from '../../assets/icons/flash-icon.svg';
import { ReactComponent as InformationIcon } from '../../assets/icons/information-icon.svg';
import { ReactComponent as LinkIcon } from '../../assets/icons/link.svg';
import { ReactComponent as SettingIcon } from '../../assets/icons/setting-icon.svg';
import { ReactComponent as ChargerIcon } from '../../assets/icons/sidebar/sidebar-device.svg';
import { ReactComponent as WifiIcon } from '../../assets/icons/wifi.svg';
// ⚠ SECURITY PROFILES DISABLED — backend not deployed yet (frontend-only rollout, PR #1130/#1143).
// Re-enable by uncommenting this block once backend security endpoints/fields ship.
/*
import DeviceSecurityPanel from '../../pages/ev-device-hub/components/security/DeviceSecurityPanel';
import { DeviceSecurity } from '../../pages/ev-device-hub/components/security/securityProfile';
*/
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { changeDateTimeFormat } from '../../shared-functions/DateTime';
import deviceHubApi from '../../utils/api/device-hub';
import BatteryLoader from '../battery-loader/BatteryLoader';
import TabToggle from '../tab-toggle/TabToggle';  

import './scss/ev-device-side-panel.scss';

interface EventDeviceSidePanelProperties {
  fetchIdFrom?: 'queryParams' | 'props';
  id?: string;
}

interface ConnectorItem {
  _id?: string;
  connectorType: string;
  status: string;
  currentType?: string;
  connectorFormat?: string;
  maxVoltage?: number | string;
  maxCurrent?: number | string;
  maxPower?: number | string;
  unit?: string;
  connectorRating?: {
    name?: string;
    currentType?: string;
    capacityKW?: string | number;
    unit?: string;
  };
}

interface DeviceSpecifications {
  powerOutput?: number | string;
  maxVoltage?: number | string;
  maxCurrent?: number | string;
  efficiencyRating?: number | string;
  operationTemperature?: string | number;
  weight?: number | string;
  displayType?: string;
}

interface DeviceFullData {
  _id: string;
  serialNumber?: string;
  tenantName?: string;
  brandName?: string;
  modelName?: string;
  skuNumber?: string;
  firmwareVersion?: string;
  currentType?: string;
  installationType?: string;
  networkConnectivity?: string[];
  plugAndCharge?: boolean;
  protocolSupport?: string;
  diagnosticProtocolTypes?: string[];
  firmwareProtocolTypes?: string[];
  certifications?: string[];
  specifications?: DeviceSpecifications;
  connectorInformation?: ConnectorItem[];
  // ⚠ SECURITY PROFILES DISABLED — backend not deployed yet. See imports above.
  // security?: DeviceSecurity;
  status?: string;
  attachedAt?: string;
  attachedBy?: string;
  deviceOwner?: string;
  deviceOwnerId?: string;
  deviceOwnerType?: string;
  tenantId?: string;
  connectionStatus?: string;
  ocppStatus?: string;
  ocppurl?: string;
  vendor?: string;
  ocppId?: string;
  [key: string]: any;
}

/** Maps connector type value to its SVG icon component */
const CONNECTOR_ICON_MAP: Record<string, React.FunctionComponent<React.SVGProps<SVGSVGElement>>> = {
  CCS1: Ccs2Icon,
  CCS2: Ccs2Icon,
  type2: Type2Icon,
  Type2: Type2Icon,
  type1: Type2Icon,
  Type1: Type2Icon,
  CHAdeMO: ChademoIcon,
  NACS: NacsIcon,
  J1772: J1772Icon,
  TYPE2_CCS2: Ccs2Icon,
};

/**
 * Returns the SVG icon for a connector type
 * @param {object} properties - Component properties
 * @param {string} properties.connectorType - Connector type name
 * @returns {React.ReactElement} Connector icon element
 */
function ConnectorIconBubble({ connectorType }: Readonly<{ connectorType: string }>) {
  const IconComponent = CONNECTOR_ICON_MAP[connectorType] || ChargerIcon;
  return <IconComponent width={24} height={24} />;
}

/**
 * Resolves the localized label for an installation type.
 * @param {string} [type] - Installation type string.
 * @param {any} translate - Translation function from useTranslation.
 * @returns {string} Localized installation type label or fallback hyphen.
 */
function getInstallationTypeLabel(
  type: string | undefined,
  translate: any,
): string {
  if (!type) return '-';
  const normalized = type.toLowerCase().trim();
  if (normalized === 'wall' || normalized === 'wallmounted' || normalized === 'wall_mounted') {
    return translate('wallMounted', { defaultValue: 'Wall Mounted' });
  }
  if (normalized === 'pedestal') {
    return translate('pedestal', { defaultValue: 'Pedestal' });
  }
  return translate(normalized, {
    defaultValue: String(type).charAt(0).toUpperCase() + String(type).slice(1),
  });
}

/**
 * Resolves the localized label for a display type.
 * @param {string} [type] - Display type string.
 * @param {any} translate - Translation function from useTranslation.
 * @returns {string} Localized display type label or fallback hyphen.
 */
function getDisplayTypeLabel(
  type: string | undefined,
  translate: any,
): string {
  if (!type) return '-';
  const normalized = type.toLowerCase().trim();
  if (normalized === 'none') {
    return translate('none', { defaultValue: 'None' });
  }
  return translate(normalized, {
    defaultValue: String(type).toUpperCase(),
  });
}

/**
 * Side Panel component for EV Device Hub
 * Displays complete device specifications, status, connectivity, technical specifications and connector details
 * @param {EventDeviceSidePanelProperties} properties - Component properties
 * @returns {React.ReactElement} EV Device Side Panel component
 */
export default function EventDeviceSidePanel(properties: Readonly<EventDeviceSidePanelProperties>) {
  const {
    fetchIdFrom = 'queryParams',
    id,
  } = properties;
  const { t } = useTranslation(['deviceHub', 'sidePanel', 'devices']);
  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const devices = useAppSelector((state) => state.deviceHub.devices);

  const [activeTab, setActiveTab] = useState<string>('deviceDetails');
  const [deviceData, setDeviceData] = useState<DeviceFullData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /**
   * Fetches single device details by ID
   * @param {string} deviceId - ID of the device to fetch
   * @returns {Promise<void>} Resolves when device details are loaded
   */
  const loadDeviceDetails = async (deviceId: string): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await deviceHubApi.fetchDeviceById(deviceId);
      const result = response?.data?.results || response?.data || response;
      setDeviceData(result);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message:
            error?.response?.data?.message ||
            error?.message ||
            t('failedToFetchConnectors', { ns: 'deviceHub' }),
          status: true,
          type: 'error',
          code: error?.response?.status,
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const resolvedId =
    fetchIdFrom === 'queryParams'
      ? searchParameters.get('deviceHubId') ||
        searchParameters.get('deviceId') ||
        searchParameters.get('id')
      : id;

  useEffect(() => {
    if (!resolvedId) return;

    loadDeviceDetails(resolvedId);
  }, [resolvedId, devices]);

  useEffect(() => {
    /**
     * Handles external device updated triggers (blacklist, whitelist, edit).
     */
    const handleDeviceUpdated = () => {
      if (resolvedId) {
        loadDeviceDetails(resolvedId);
      }
    };

    window.addEventListener('ev-device-updated', handleDeviceUpdated);
    window.addEventListener('pageshow', handleDeviceUpdated);
    window.addEventListener('focus', handleDeviceUpdated);

    return () => {
      window.removeEventListener('ev-device-updated', handleDeviceUpdated);
      window.removeEventListener('pageshow', handleDeviceUpdated);
      window.removeEventListener('focus', handleDeviceUpdated);
    };
  }, [resolvedId]);

  const isBlacklisted = deviceData?.status === 'blacklisted';
  const isOnline =
    String(deviceData?.connectionStatus ?? '').toLowerCase() === 'online';
  const isAttached = Boolean(deviceData?.deviceOwner && deviceData?.attachedBy);

  if (isLoading) {
    return (
      <div className="ev-device-side-panel-wrap">
        <div className="panel-loader-container">
          <BatteryLoader />
        </div>
      </div>
    );
  }

  if (!deviceData) {
    return (
      <div className="ev-device-side-panel-wrap">
        <p className="empty-state-text">{t('noDataAvailable', 'No Data Available')}</p>
      </div>
    );
  }

  return (
    <div className="ev-device-side-panel-wrap">
      {/* Navigation Tabs using TabToggle */}
      <div className="tab-toggle-container">
        <TabToggle
          tabs={[
            { label: t('deviceDetails', 'Device Details'), value: 'deviceDetails' },
            { label: t('technicalInformation', 'Technical Information'), value: 'technicalInformation' },
            { label: t('connectorDetails', 'Connector Details'), value: 'connectorDetails' },
          ]}
          activeTab={activeTab}
          defaultTab="deviceDetails"
          onChange={(value) => setActiveTab(value)}
          isLabelValue
        />
      </div>

      {/* Main Content Area */}
      <div className="side-panel-scroll-content">
        {activeTab === 'deviceDetails' && (
          <>
            {/* Section 1: Basic Information */}
            <div className="info-section-card">
              <div className="section-card-header">
                <div className="header-title-wrap">
                  <span className="header-icon">
                    <InformationIcon />
                  </span>
                  <h4 className="section-title">{t('basicInformation', 'Basic Information')}</h4>
                </div>
                <div className="badges-row-right">
                  <span className={`status-pill ${isBlacklisted ? 'status-red' : 'status-green'}`}>
                    <span className="dot" />
                    {isBlacklisted ? t('blacklisted', 'Blacklisted') : t('whitelisted', 'Whitelisted')}
                  </span>
                  <span className={`status-pill ${isOnline ? 'status-green' : 'status-red'}`}>
                    <span className="dot" />
                    {isOnline ? t('online', 'Online') : t('offline', 'Offline')}
                  </span>
                </div>
              </div>
              <div className="info-grid grid-2">
                <div className="grid-cell">
                  <span className="cell-label">{t('deviceId', 'Device Id')}</span>
                  <span className="cell-value">{deviceData.skuNumber || deviceData.serialNumber || '-'}</span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">{t('brandName', 'Brand Name')}</span>
                  <span className="cell-value">{deviceData.brandName || '-'}</span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">{t('modelName', 'Model Name')}</span>
                  <span className="cell-value">{deviceData.modelName || '-'}</span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">{t('currentType', 'Current Type')}</span>
                  <span className={`cell-value type-badge-${String(deviceData.currentType || 'ac').toLowerCase()}`}>
                    {String(deviceData.currentType || 'AC').toUpperCase()}
                  </span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">{t('serialNumber', 'Serial Number')}</span>
                  <span className="cell-value">{deviceData.serialNumber || '-'}</span>
                </div>
                {/* <div className="grid-cell">
                  <span className="cell-label">{t('vendor', 'Vendor')}</span>
                  <span className="cell-value">{deviceData.vendor || '-'}</span>
                </div> */}
                <div className="grid-cell">
                  <span className="cell-label">{t('firmwareVersion', 'Firmware Version')}</span>
                  <span className="cell-value">{deviceData.firmwareVersion || '-'}</span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">{t('installationType', 'Installation Type')}</span>
                  <span className="cell-value">{getInstallationTypeLabel(deviceData.installationType, t)}</span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">{t('protocol', 'Protocol')}</span>
                  <span className="cell-value">{deviceData.protocolSupport || '-'}</span>
                </div>
                {/* <div className="grid-cell">
                  <span className="cell-label">{t('ocppId', 'OCPP ID')}</span>
                  <span className="cell-value">{deviceData.ocppId || '-'}</span>
                </div> */}
                <div className="grid-cell">
                  <span className="cell-label">{t('ocppUrl', 'OCPP URL')}</span>
                  <span className="cell-value ltr-value">{deviceData.ocppurl || '-'}</span>
                </div>
              </div>
            </div>

            {/* Section 2: Attachment Status */}
            <div className="info-section-card">
              <div className="section-card-header">
                <div className="header-title-wrap">
                  <span className="header-icon">
                    <LinkIcon />
                  </span>
                  <h4 className="section-title">{t('attachmentStatus', 'Attachment Status')}</h4>
                </div>
                <div className="section-header-actions">
                  <span className={`status-pill ${isAttached ? 'status-green' : 'status-red'}`}>
                    <span className="dot" />
                    {isAttached ? t('attached', 'Attached') : t('notAttached', 'Not Attached')}
                  </span>
                </div>
              </div>
              <div className="info-grid grid-3">
                <div className="grid-cell">
                  <span className="cell-label">{t('attachedAt', 'Attached At')}</span>
                  <span className="cell-value">
                    {deviceData.attachedAt ? changeDateTimeFormat(deviceData.attachedAt) : '-'}
                  </span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">{t('attachedEntity', 'Attached Entity')}</span>
                  <span className="cell-value">
                    {deviceData.deviceOwnerType && deviceData.attachedBy
                      ? `${deviceData.deviceOwnerType === 'commercial' ? t('cpo', 'CPO') : t('emsp', 'EMSP')} (${deviceData.attachedBy})`
                      : '-'}
                  </span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">{t('attachedBy', 'Attached By')}</span>
                  <span className="cell-value">{deviceData.attachedBy || '-'}</span>
                </div>
              </div>
            </div>

            {/* Section 3: Network & Features */}
            <div className="info-section-card">
              <div className="section-card-header">
                <div className="header-title-wrap">
                  <span className="header-icon">
                    <WifiIcon />
                  </span>
                  <h4 className="section-title">{t('networkAndFeatures', 'Network & Features')}</h4>
                </div>
              </div>
              <div className="info-grid grid-2">
                <div className="grid-cell">
                  <span className="cell-label">{t('networkConnectivity', 'Network Connectivity')}</span>
                  <span className="cell-value">
                    {Array.isArray(deviceData.networkConnectivity)
                      ? deviceData.networkConnectivity.join(', ')
                      : deviceData.networkConnectivity || '-'}
                  </span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">{t('plugAndCharge', 'Plug & Charge')}</span>
                  <span className="cell-value">
                    {deviceData.plugAndCharge ? t('enabled', 'Enabled') : t('disabled', 'Disabled')}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'connectorDetails' && (
          /* Connectors Tab View */
          <div className="connectors-list-wrap">
            {(!deviceData.connectorInformation || deviceData.connectorInformation.length === 0) ? (
              <p className="empty-state-text">{t('noConnectorsFound', 'No Connectors Found')}</p>
            ) : (
              deviceData.connectorInformation.map((connector: ConnectorItem, index: number) => {
                const ratingObject = typeof connector.connectorRating === 'object' ? connector.connectorRating : null;
                const capacity = ratingObject?.capacityKW || connector.maxPower || '';
                const unit = ratingObject?.unit || connector.unit || 'kW';
                const currentType = (ratingObject?.currentType || connector.currentType || 'AC').toUpperCase();
                const rawFormat = connector.connectorFormat;
                const format = rawFormat
                  ? t(rawFormat.toLowerCase().trim(), {
                      defaultValue: rawFormat.charAt(0).toUpperCase() + rawFormat.slice(1),
                    })
                  : '-';
                const connStatus = connector.status || 'UNKNOWN';
                const localizedStatus = t(connStatus.toLowerCase().trim(), {
                  defaultValue: connStatus,
                });

                return (
                  <div className="connector-card-item" key={connector._id || index}>
                    <div className="card-header-row">
                      <div className="connector-info-left">
                        <div className="connector-icon-bubble">
                          <ConnectorIconBubble connectorType={connector.connectorType} />
                        </div>
                        <div className="connector-text">
                          <h4 className="connector-title">{connector.connectorType || '-'}</h4>
                          <span className="connector-subtitle">
                            {currentType} {capacity} {unit}
                          </span>
                        </div>
                      </div>
                      <span className={`connector-status-badge ${connStatus.toLowerCase()}`}>
                        {localizedStatus}
                      </span>
                    </div>
                    <div className="card-divider" />
                    <div className="card-specs-grid">
                      <div className="spec-col">
                        <span className="spec-label">{t('connectorFormat', 'Connector Format')}</span>
                        <span className="spec-value">{format}</span>
                      </div>
                      <div className="spec-col">
                        <span className="spec-label">{t('maxVoltage', 'Max Voltage')}</span>
                        <span className="spec-value">{connector.maxVoltage ? `${connector.maxVoltage} V` : '-'}</span>
                      </div>
                      <div className="spec-col">
                        <span className="spec-label">{t('maxCurrent', 'Max Current')}</span>
                        <span className="spec-value">{connector.maxCurrent ? `${connector.maxCurrent} kW` : '-'}</span>
                      </div>
                      <div className="spec-col">
                        <span className="spec-label">{t('currentType', 'Current Type')}</span>
                        <span className="spec-value">{currentType}</span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {activeTab === 'technicalInformation' && (
          <>
            {/* Specifications Section */}
            <div className="info-section-card">
              <div className="section-card-header">
                <div className="header-title-wrap">
                  <span className="header-icon">
                    <FlashIcon />
                  </span>
                  <h4 className="section-title">{t('specifications', 'Specifications')}</h4>
                </div>
              </div>
              <div className="info-grid grid-2">
                <div className="grid-cell">
                  <span className="cell-label">{t('powerOutput', 'Power Output (kW)')}</span>
                  <span className="cell-value">
                    {deviceData.specifications?.powerOutput != null
                      ? `${deviceData.specifications.powerOutput} kW`
                      : '-'}
                  </span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">{t('maxVoltageV', 'Max Voltage (V)')}</span>
                  <span className="cell-value">
                    {deviceData.specifications?.maxVoltage != null
                      ? `${deviceData.specifications.maxVoltage} V`
                      : '-'}
                  </span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">{t('maxCurrentA', 'Max Current (A)')}</span>
                  <span className="cell-value">
                    {deviceData.specifications?.maxCurrent != null
                      ? `${deviceData.specifications.maxCurrent} A`
                      : '-'}
                  </span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">{t('efficiencyRating', 'Efficiency Rating (%)')}</span>
                  <span className="cell-value">
                    {deviceData.specifications?.efficiencyRating != null
                      ? `${deviceData.specifications.efficiencyRating}%`
                      : '-'}
                  </span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">{t('operationTemperature', 'Operation Temperature')}</span>
                  <span className="cell-value">
                    {deviceData.specifications?.operationTemperature
                      ? `${deviceData.specifications.operationTemperature} °C`
                      : '-'}
                  </span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">{t('weight', 'Weight (Kg)')}</span>
                  <span className="cell-value">
                    {deviceData.specifications?.weight != null
                      ? `${deviceData.specifications.weight} kg`
                      : '-'}
                  </span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">{t('displayType', 'Display Type')}</span>
                  <span className="cell-value">
                    {getDisplayTypeLabel(deviceData.specifications?.displayType, t)}
                  </span>
                </div>
              </div>
            </div>

            {/* Protocols & Connectivity Section */}
            <div className="info-section-card">
              <div className="section-card-header">
                <div className="header-title-wrap">
                  <span className="header-icon">
                    <WifiIcon />
                  </span>
                  <h4 className="section-title">
                    {t('protocolsAndConnectivity', 'Protocols & Connectivity')}
                  </h4>
                </div>
              </div>
              <div className="info-grid grid-2">
                <div className="grid-cell">
                  <span className="cell-label">{t('protocolSupport', 'Protocol Support')}</span>
                  <span className="cell-value">
                    {deviceData.protocolSupport
                      ? String(deviceData.protocolSupport).toUpperCase()
                      : '-'}
                  </span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">{t('networkConnectivity', 'Network Connectivity')}</span>
                  <span className="cell-value">
                    {Array.isArray(deviceData.networkConnectivity) &&
                      deviceData.networkConnectivity.length > 0
                      ? deviceData.networkConnectivity
                        .map((net: string) => net.toUpperCase())
                        .join(', ')
                      : deviceData.networkConnectivity || '-'}
                  </span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">
                    {t('diagnosticProtocolTypes', 'Diagnostic Protocol Types')}
                  </span>
                  <span className="cell-value">
                    {Array.isArray(deviceData.diagnosticProtocolTypes) &&
                      deviceData.diagnosticProtocolTypes.length > 0
                      ? deviceData.diagnosticProtocolTypes.join(', ')
                      : deviceData.diagnosticProtocolTypes || '-'}
                  </span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">
                    {t('firmwareProtocolTypes', 'Firmware Protocol Types')}
                  </span>
                  <span className="cell-value">
                    {Array.isArray(deviceData.firmwareProtocolTypes) &&
                      deviceData.firmwareProtocolTypes.length > 0
                      ? deviceData.firmwareProtocolTypes.join(', ')
                      : deviceData.firmwareProtocolTypes || '-'}
                  </span>
                </div>
              </div>
            </div>

            {/* Device Configuration & Certifications */}
            <div className="info-section-card">
              <div className="section-card-header">
                <div className="header-title-wrap">
                  <span className="header-icon">
                    <SettingIcon />
                  </span>
                  <h4 className="section-title">
                    {t('deviceConfiguration', 'Device Configuration')}
                  </h4>
                </div>
              </div>
              <div className="info-grid grid-2">
                <div className="grid-cell">
                  <span className="cell-label">{t('currentType', 'Current Type')}</span>
                  <span
                    className={`cell-value type-badge-${String(deviceData.currentType || 'ac').toLowerCase()}`}>
                    {String(deviceData.currentType || 'AC').toUpperCase()}
                  </span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">{t('installationType', 'Installation Type')}</span>
                  <span className="cell-value">
                    {getInstallationTypeLabel(deviceData.installationType, t)}
                  </span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">{t('plugAndCharge', 'Plug & Charge')}</span>
                  <span className="cell-value">
                    {deviceData.plugAndCharge ? t('yes', 'Yes') : t('no', 'No')}
                  </span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">{t('firmwareVersion', 'Firmware Version')}</span>
                  <span className="cell-value">{deviceData.firmwareVersion || '-'}</span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">{t('skuNumber', 'SKU Number')}</span>
                  <span className="cell-value">{deviceData.skuNumber || '-'}</span>
                </div>
                <div className="grid-cell">
                  <span className="cell-label">{t('certifications', 'Certifications')}</span>
                  <span className="cell-value">
                    {Array.isArray(deviceData.certifications) &&
                      deviceData.certifications.length > 0 ? (
                      <div className="certifications-list">
                        {deviceData.certifications.map((cert: string) => (
                          <span className="cert-badge" key={cert}>
                            {cert.toUpperCase()}
                          </span>
                        ))}
                      </div>
                    ) : (
                      '-'
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* ⚠ SECURITY PROFILES DISABLED — backend not deployed yet. See imports above.
            <DeviceSecurityPanel
              deviceId={deviceData._id}
              security={deviceData.security}
              onUpdated={() => loadDeviceDetails(deviceData._id)}
            />
            */}
          </>
        )}
      </div>
    </div>
  );
}

EventDeviceSidePanel.defaultProps = {
  fetchIdFrom: 'queryParams',
  id: undefined,
};
